import React, { useState, useEffect } from 'react';
import Button from './Button';

interface StaticMapComponentProps {
  lat?: number;
  lng?: number;
  address: string;
  name?: string;
  googleMapsUrl: string;
  height?: number;
  width?: number | string;
  zoom?: number;
  placeId?: string;
  isSpanish?: boolean;
}

/**
 * A component that displays a Google Maps iframe with styling matching the design requirements
 */
function StaticMapComponent({
  address,
  name,
  googleMapsUrl,
  zoom = 15,
  placeId,
  isSpanish = false,
}: StaticMapComponentProps) {
  const [isClient, setIsClient] = useState<boolean>(false);

  // Extract place ID from URL if not provided directly
  const extractPlaceId = (url: string): string | null => {
    // Try to extract place_id from the URL
    const placeIdMatch = url.match(/place_id=([^&]+)/);
    if (placeIdMatch) return placeIdMatch[1];

    // Alternative formats
    const placeIdMatch2 = url.match(/query_place_id=([^&]+)/);
    if (placeIdMatch2) return placeIdMatch2[1];

    return null;
  };

  // Set isClient to true when component mounts
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Generate the embed URL for the iframe
  const generateEmbedUrl = () => {
    // Get API key from environment variable
    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '';

    const extractedPlaceId = placeId || extractPlaceId(googleMapsUrl);

    if (extractedPlaceId) {
      return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=place_id%3A${extractedPlaceId}&zoom=${zoom}`;
    } else {
      // Fallback to using the address if no place ID
      return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(
        address
      )}&zoom=${zoom}`;
    }
  };

  // Early return for server-side rendering
  if (!isClient) {
    return (
      <div className="jsx-33066475 gmaps">
        <div className="rounded overflow-hidden block">
          <div
            className="bg-gray-100 flex items-center justify-center"
            style={{ height: '199px', width: '100%' }}
          >
            <span>{name || address}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="jsx-33066475 gmaps">
      <iframe
        src={generateEmbedUrl()}
        tabIndex={-1}
        loading="lazy"
        style={{
          width: '100%',
          border: 'none',
          height: '199px',
          display: 'flex',
          pointerEvents: 'none',
          borderRadius: '0.5rem',
        }}
        className="adaptive-inverted-filter"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
}

export default React.memo(StaticMapComponent);
